import i18n from "../../../i18n";
import { isServerStartGuideStatus } from "./startGuideStatusSource";

const completionOf = (stepId, steps) =>
  Boolean(steps.find((step) => step.id === stepId)?.complete);

export function isStartGuideSkippable(step) {
  if (!step || step.skippable === false || step.complete || step.category === "learn" || step.category === "required") {
    return false;
  }
  return Boolean(
    step.optional || step.category === "recommended" || step.category === "optional"
  );
}

/** Required, then desirable, then optional. Learn stays in its own group. */
export function startGuideStepRank(step) {
  if (step?.category === "required") return 0;
  if (step?.category === "recommended") return 1;
  if (step?.category === "optional" || step?.optional) return 2;
  return 3;
}

/**
 * Steps the server returned, in the local guide order.
 * Numeric object keys are enumerated before strings, so Object.keys would
 * put "subscription" after "4". The local list is the order the API documents.
 */
export function orderStepsByServerKeys(defaultSteps = [], serverSteps = {}) {
  const present = new Set(Object.keys(serverSteps || {}).map(String));
  const knownIds = new Set(defaultSteps.map((step) => String(step.id)));
  const ordered = defaultSteps.filter((step) => present.has(String(step.id)));
  Object.keys(serverSteps || {}).forEach((id) => {
    if (knownIds.has(String(id))) return;
    ordered.push({
      id: String(id),
      step: String(id),
      name: String(id),
      category: "optional",
      optional: true,
      skippable: id !== "4" && id !== "subscription",
    });
  });
  return ordered;
}

export function countSettledProgress(steps = []) {
  const counted = steps.filter((step) => step.category !== "learn");
  const total = counted.length;
  const done = counted.filter((step) => step.complete || step.skipped).length;
  return {
    done,
    total,
    percent: total === 0 ? 0 : Math.round((done / total) * 100),
    complete: total > 0 && done === total,
  };
}

export function orderStartGuideSteps(steps = []) {
  return steps
    .map((step, index) => ({ step, index }))
    .sort(
      (a, b) =>
        startGuideStepRank(a.step) - startGuideStepRank(b.step) || a.index - b.index
    )
    .map((item) => item.step);
}

/**
 * Merges guide content with either the client record or a server payload.
 * A signal may force a step open or closed when the browser already knows
 * the underlying data. `null` means "no signal, use the stored flag".
 */
function fromServer(steps, serverStatus) {
  const serverSteps = serverStatus?.steps || {};
  return steps.map((step) => {
    if (step.category === "learn") {
      return { ...step, complete: false, skipped: false, locked: false, lockReason: "" };
    }
    const serverStep = serverSteps[step.id];
    const complete = Boolean(serverStep?.complete);
    const locked = Boolean(serverStep?.locked);
    return {
      ...step,
      complete,
      skipped: Boolean(serverStep?.skipped) && !complete,
      locked,
      lockReason: locked
        ? serverStep?.lockReason || step.lockReason || i18n.t("corePages.startGuide.completePreviousStep")
        : "",
    };
  });
}

export function resolveStartGuideSteps({
  steps = [],
  clientSteps = {},
  serverStatus = null,
  signals = {},
}) {
  if (isServerStartGuideStatus()) {
    return fromServer(steps, serverStatus);
  }

  const useServer = isServerStartGuideStatus() && serverStatus?.steps;

  const withCompletion = steps.map((step) => {
    if (step.category === "learn") {
      return { ...step, complete: false, locked: false, lockReason: "" };
    }

    const serverStep = useServer ? serverStatus.steps[step.id] : null;
    let complete = false;
    const client = clientSteps[step.id] || {};
    const skipped =
      Boolean(client.skipped) &&
      step.category !== "required" &&
      step.category !== "learn" &&
      (step.optional || step.category === "recommended" || step.category === "optional");
    const visited = step.category === "required" && Boolean(client.visited);

    if (serverStep && typeof serverStep.complete === "boolean") {
      complete = serverStep.complete;
    } else {
      const signal = signals[step.id];
      const signalValue = typeof signal === "function" ? signal() : signal;
      if (signalValue === true || visited) complete = true;
      else if (skipped) complete = false;
      else if (signalValue === false) complete = false;
      else complete = Boolean(client.complete);
    }

    return {
      ...step,
      complete,
      skipped: skipped && !complete,
      locked: false,
      lockReason: step.lockReason || "",
    };
  });

  return withCompletion.map((step) => {
    if (step.category === "learn") return step;

    const serverStep = useServer ? serverStatus.steps[step.id] : null;
    if (serverStep && typeof serverStep.locked === "boolean") {
      return {
        ...step,
        locked: serverStep.locked,
        lockReason: serverStep.lockReason || step.lockReason || "",
      };
    }

    const missing = (step.requires || []).filter((id) => !completionOf(id, withCompletion));
    if (missing.length === 0) return { ...step, locked: false };

    return {
      ...step,
      locked: true,
      lockReason: step.lockReason || i18n.t("corePages.startGuide.completePreviousStep"),
    };
  });
}

export function countRequiredProgress(steps = []) {
  const required = steps.filter((step) => step.category === "required");
  const total = required.length;
  const done = required.filter((step) => step.complete).length;
  return {
    done,
    total,
    percent: total === 0 ? 0 : Math.round((done / total) * 100),
    complete: total > 0 && done === total,
  };
}
